/**
 * Upwork Start Call Page
 *
 * Dedicated call-start page for Upwork escalation flows.
 * Fetches eval config to display launch_context cards (recommended action,
 * provider directory) alongside the standard call-initiation form.
 *
 * URL: /upwork/start?matter_id=...&approval_id=...&to_number=...
 */
import { Info, Loader2, Phone, Star } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAgentflowGetEvalFormQuery } from '@/services/api/generated/agentflowEvalService';
import { useGetMatterQuery } from '@/services/api/mattersService';

// ---------------------------------------------------------------------------
// Types for launch_context (typed from backend contract)
// ---------------------------------------------------------------------------

interface ProviderDirectoryRecommended {
  channel_type?: string;
  value?: string;
  verification_status?: string;
  source?: string;
  evidence_summary?: string;
}

interface ProviderDirectory {
  provider_service_id?: string;
  provider_name?: string;
  scope?: string;
  coverage_status?: string;
  voiceai_followup_phone?: string | null;
  recommended?: ProviderDirectoryRecommended;
  alternates?: ProviderDirectoryRecommended[];
}

interface LaunchContext {
  recommended_action?: string;
  provider_directory?: ProviderDirectory;
}

// ---------------------------------------------------------------------------
// Dark theme constants (shared with AgentflowEvalPage)
// ---------------------------------------------------------------------------

const DARK_INPUT =
  'border-[#1f2a44] bg-[#0a1228] text-white placeholder:text-gray-500 focus-visible:ring-blue-500';
const DARK_LABEL = 'text-[#9aa3b2]';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const RecommendedActionCard: React.FC<{ action: string }> = ({ action }) => (
  <div className="rounded-lg border border-green-600/40 bg-[linear-gradient(180deg,rgba(34,197,94,0.04),rgba(34,197,94,0))] p-4 space-y-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
        <span className="text-xs font-bold uppercase tracking-wide text-green-400">
          Recommended Action
        </span>
      </div>
      <span className="text-xs text-[#9aa3b2]">from task notes</span>
    </div>
    <p className="text-sm text-[#c8ced9] leading-relaxed">{action}</p>
  </div>
);

const ProviderDirectoryCard: React.FC<{
  directory: ProviderDirectory;
}> = ({ directory }) => {
  const rec = directory.recommended;
  const isVerified = rec?.verification_status === 'VERIFIED';

  const formatContact = () => {
    if (!rec?.value) return null;
    const label = rec.channel_type === 'FAX' ? 'Fax' : 'Phone';
    // Format phone number for display
    const digits = rec.value.replace(/\D/g, '');
    let formatted = rec.value;
    if (digits.length === 11 && digits.startsWith('1')) {
      formatted = `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    } else if (digits.length === 10) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return `${label}: ${formatted}`;
  };

  return (
    <div className="rounded-lg border border-green-600/40 bg-[linear-gradient(180deg,rgba(34,197,94,0.04),rgba(34,197,94,0))] p-4 space-y-1">
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 text-blue-400" />
        <span className="text-xs font-bold uppercase tracking-wide text-blue-400">
          Provider Directory
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-white">
          {directory.provider_name}
        </span>
        {isVerified && (
          <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-green-900/40 text-green-400 border border-green-600/30">
            Verified contact
          </span>
        )}
      </div>
      {formatContact() && (
        <p className="text-sm text-[#9aa3b2]">{formatContact()}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const UpworkStartPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const matterId = searchParams.get('matter_id') || '';
  const toNumberQP = searchParams.get('to_number') || '';
  const workflowId = searchParams.get('workflow_id') || '';
  const runId = searchParams.get('run_id') || '';
  const coverageId = searchParams.get('coverage_id') || '';
  const taskType = searchParams.get('task_type') || '';
  const taskId = searchParams.get('task_id') || '';
  const treatmentRequestId = searchParams.get('treatment_request_id') || '';
  const approvalId = searchParams.get('approval_id') || '';

  // Fetch matter for context card
  const { data: matter } = useGetMatterQuery(matterId, { skip: !matterId });

  // Fetch eval config for launch_context
  const { data: evalConfig } = useAgentflowGetEvalFormQuery(
    { matterId, approvalId },
    { skip: !matterId || !approvalId },
  );
  const launchContext = evalConfig?.launch_context as LaunchContext | undefined;

  const [toNumber, setToNumber] = React.useState<string>(toNumberQP);
  const [fromNumber, setFromNumber] = React.useState<string>('');
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!matterId || !toNumber || !fromNumber) {
      toast.error('Please provide all required fields.');
      return;
    }
    setIsSubmitting(true);
    try {
      const qs = new URLSearchParams({
        matter_id: matterId,
        to_number: toNumber,
        from_number: fromNumber,
        redirect: '1',
      });
      if (workflowId) qs.set('workflow_id', workflowId);
      if (runId) qs.set('run_id', runId);
      if (coverageId) qs.set('coverage_id', coverageId);
      if (taskType) qs.set('task_type', taskType);
      if (taskId) qs.set('task_id', taskId);
      if (treatmentRequestId)
        qs.set('treatment_request_id', treatmentRequestId);
      if (approvalId) qs.set('approval_id', approvalId);

      const backend =
        (import.meta.env.VITE_APP_DJANGO_BACKEND_HOST as string) ||
        'http://localhost:8000';
      const backendRoot = backend.replace(/\/$/, '');
      window.location.href = `${backendRoot}/api/integrations/calls/launch?${qs.toString()}`;
    } catch (err: unknown) {
      const message =
        (err as { message?: string })?.message ||
        'Please check the numbers and try again.';
      toast.error('Failed to initiate call', { description: message });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl border-[#1f2a44] bg-gradient-to-b from-[#121936] to-[#0f1731]">
        <CardHeader className="border-b border-[#1f2a44]">
          <CardTitle className="text-white italic">Start a Call</CardTitle>
          <CardDescription className="text-[#9aa3b2]">
            Provide the numbers; we&apos;ll include the matter context for you.
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6 space-y-4">
          {/* Recommended Action card */}
          {launchContext?.recommended_action && (
            <RecommendedActionCard action={launchContext.recommended_action} />
          )}

          {/* Provider Directory card */}
          {launchContext?.provider_directory && (
            <ProviderDirectoryCard
              directory={launchContext.provider_directory}
            />
          )}

          {/* Matter context */}
          {matter && (
            <div className="rounded-lg border border-[#1f2a44] p-3 text-sm text-[#9aa3b2] bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))]">
              <span className="text-white font-medium">Matter:</span>{' '}
              {matter.name}
            </div>
          )}

          {/* Call form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="to_number" className={DARK_LABEL}>
                To number
              </Label>
              <Input
                id="to_number"
                name="to_number"
                value={toNumber}
                onChange={(e) => setToNumber(e.target.value)}
                placeholder="+1XXXXXXXXXX or (555) 555-5555"
                required
                aria-label="Destination phone number"
                className={DARK_INPUT}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="from_number" className={DARK_LABEL}>
                From number
              </Label>
              <Input
                id="from_number"
                name="from_number"
                value={fromNumber}
                onChange={(e) => setFromNumber(e.target.value)}
                placeholder="+1XXXXXXXXXX or (555) 555-5555"
                required
                aria-label="Your caller ID"
                className={DARK_INPUT}
              />
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Initiating...
                  </>
                ) : (
                  <>
                    <Phone className="h-4 w-4 mr-2" />
                    Initiate Call
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UpworkStartPage;
